/**
 * client/src/components/providers/theme-provider.tsx
 *
 * Wraps next-themes ThemeProvider with project defaults:
 *   - attribute="data-theme" — matches the Tailwind token plugin's [data-theme="light"] selector
 *   - defaultTheme="dark"    — dark-first per design system spec
 *   - enableSystem=false     — pilot is pinned to dark (#1587 owner decision, 2026-07-14).
 *     ~25 surfaces still hardcode dark-navy hexes instead of theme tokens, so a
 *     system-light first visit renders those surfaces as broken dark islands
 *     against an otherwise light shell (DESIGN-SYSTEM.md §5.2). Migrating those
 *     hardcoded hexes to tokens is tracked separately (#1594, post-launch); this
 *     is a deliberate 1-line pin, not that migration.
 *   - nonce                 — forwarded to next-themes' own <style> injector
 *     (its disableTransitionOnChange snippet, #1499). Currently DORMANT here
 *     since disableTransitionOnChange={false}, but wired so the CSP
 *     style-src-elem nonce (server/middleware/security-headers.ts) covers it
 *     if that ever flips on — see client/src/lib/csp-nonce.ts.
 */

import { ThemeProvider as NextThemeProvider, useTheme as useNextTheme } from "next-themes";
import type { ReactNode } from "react";

interface Props {
  children: ReactNode;
  nonce?: string;
}

export function ThemeProvider({ children, nonce }: Props) {
  return (
    <NextThemeProvider
      attribute="data-theme"
      defaultTheme="dark"
      enableSystem={false}
      disableTransitionOnChange={false}
      nonce={nonce}
    >
      {children}
    </NextThemeProvider>
  );
}

export { useNextTheme as useTheme };
